import React, { useEffect, useState } from "react";

import Header from "../../../components/Header";
import TabNavbar from "../../../components/Tabbar";
import { MY_TEAM_TABS } from "../../../utils/TabbarData";
import SearchWithFilter from "../../../components/SearchWithFilter";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import SpinnerLoader from "../../../components/SpinnerLoader";
import HomeButton from "../../../components/HomeButton";
import {
  getManagerApplications,
  updateManagerApplicationStatus,
} from "../../../redux/slices/manager.applications.slice";
import Pagination from "../../../components/Pagination";
import { FaX } from "react-icons/fa6";
import SaveConfirmModal from "../../../components/modal/SaveConfirmModal";
import { BsFilter } from "react-icons/bs";
import { extractCareerLevelNumber } from "../../../utils/helper";
import EventDetailsModal from "../../Sessions/components/EventDetailsModal";

const STATUS_MAP: any = {
  APPLIED: {
    label: "PENDING MANAGER CONFIRMATION",
    color: "bg-orange-500 text-white",
  },
  APPROVED_BY_MANAGER: {
    label: "MANAGER APPROVED",
    color: "bg-green-500 text-white",
  },
  REJECTED_BY_MANAGER: {
    label: "MANAGER DECLINED",
    color: "bg-red-500 text-white",
  },
  APPLICATION_REJECTED_BY_LEARNER: {
    label: "USER DECLINED",
    color: "bg-red-500 text-white",
  },
  WAITING: {
    label: "WAITING LIST",
    color: "bg-blue-500 text-white",
  },
  DRAFTED: {
    label: "DRAFTED",
    color: "bg-gray-500 text-white",
  },
  CONFIRMED_BY_LEARNER: {
    label: "CONFIRMED BY LEARNER",
    color: "bg-green-500 text-white",
  },
  CONFIRMED_BY_HR: {
    label: "CONFIRMED BY HR",
    color: "bg-green-600 text-white",
  },
  CONFIRMED_BY_MANAGER: {
    label: "CONFIRMED BY MANAGER",
    color: "bg-green-700 text-white",
  },
  HR_APPROVAL_PENDING: {
    label: "HR APPROVAL PENDING",
    color: "bg-orange-500 text-white",
  },
  APPROVED_BY_HR: {
    label: "HR APPROVED",
    color: "bg-green-500 text-white",
  },
  ELIGIBLE: {
    label: "Eligible",
    color: "bg-green-500 text-white",
  },
  NOT_ELIGIBLE: {
    label: "Not Eligible",
    color: "bg-gray-500 text-white",
  },
};

const TeamApplications = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { loading, managerCourses, updateLoading } = useSelector(
    (state: RootState) => state.managerApplications
  );

  const [modalData, setModalData] = useState<any[]>([]); // Modal data for events
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isCancelApplicationModalOpen, setIsCancelApplicationModalOpen] =
    useState(false);
  const [currentCourseId, setCurrentCourseId] = useState<number>(0);
  const [currentUserId, setCurrentUserId] = useState<number>(0);

  const [filteredCourses, setFilteredCourses] = useState<any[]>(managerCourses);
  const [applicationStatusFilter, setApplicationStatusFilter] = useState("");
  const [courseNameFilter, setCourseNameFilter] = useState(""); // string input
  const [learnerNameFilter, setLearnerNameFilter] = useState(""); // string input
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [currentCourseName, setCurrentCourseName] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentSessionName, setCurrentSessionName] = useState("");

  const totalPages =
    filteredCourses?.length > 0
      ? Math.ceil(filteredCourses?.length / itemsPerPage)
      : 1;

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    dispatch(getManagerApplications({ status: "MANAGER_TEAM_APPLICATIONS" }));
  }, [dispatch]);

  const paginatedData =
    filteredCourses?.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    ) || [];

  useEffect(() => {
    setFilteredCourses(managerCourses);
  }, [managerCourses]);

  const handleCancelApplication = async () => {
    const payload = {
      userId: currentUserId,
      courseId: currentCourseId,
      status: "REJECTED_BY_MANAGER",
    };
    await dispatch(updateManagerApplicationStatus(payload));
    setIsCancelApplicationModalOpen(false);
    setCurrentCourseName("");
    setCurrentUserName("");
    await dispatch(
      getManagerApplications({ status: "MANAGER_TEAM_APPLICATIONS" })
    );
  };

  const applyFilters = () => {
    let filteredData = managerCourses;

    // Search Term filter on course_name
    if (searchTerm.trim() !== "") {
      filteredData = filteredData.filter((course: any) => {
        return course.applications[0].user_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    }

    // Filter by Type (category_type)
    if (applicationStatusFilter !== "") {
      filteredData = filteredData.filter((course: any) => {
        return (
          course.applications[0].application_status ==
          applicationStatusFilter.toUpperCase()
        );
      });
    }

    // Filter by Course Name (contains, case insensitive)
    if (courseNameFilter.trim() !== "") {
      filteredData = filteredData.filter((course: any) =>
        course.course_name
          ?.toLowerCase()
          .includes(courseNameFilter.trim().toLowerCase())
      );
    }

    // Filter by Learner Name (contains, case insensitive)
    if (learnerNameFilter.trim() !== "") {
      filteredData = filteredData.filter((course: any) =>
        course.applications[0].user_name
          ?.toLowerCase()
          .includes(learnerNameFilter.trim().toLowerCase())
      );
    }

    setFilteredCourses(filteredData);
    setCurrentPage(1);
  };

  // Parse and set modal data for events
  const handleOpenModal = (events: string) => {
    // Step 1: Split by "," only outside of "({ })"
    // const eventBlocks = events.split(/(?<=\)}),/); // Match ", " only outside the "({ })"
    const eventBlocks = events.split(/;\s*/);
    const parsedEvents = eventBlocks.map((block) => {
      // Step 2: Split by "({"
      const [eventName, detailsBlock] = block.split("{(");

      // Step 3: Remove closing ")}" and split details inside "({ })" by ","
      const details = detailsBlock
        ?.replace(")}", "")
        ?.split(",")
        .map((item) => item.trim());

      // Step 4: Map details to fields
      const [date, time, mode, location, classroom, ...instructorsBlock] =
        details || [];

      // Step 5: Clean instructors
      // Step 5: Extract and clean instructors
      const instructors = instructorsBlock
        .join(", ") // Join remaining parts to recreate full instructors string
        .replace("Instructors:", "") // Remove the "Instructors:" prefix
        .trim();

      return {
        eventName: eventName?.trim(),
        date: date || null,
        time: time
          ? time
              .split("-")
              .map((t) => t.trim().slice(0, 5))
              .join("-")
          : null,
        mode: mode || null,
        location: location || null,
        classroom: classroom || null,
        instructors: instructors || null,
      };
    });

    setModalData(parsedEvents);
    setIsModalOpen(true);
  };

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    applicationStatusFilter,
    courseNameFilter,
    learnerNameFilter,
    managerCourses,
  ]);

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start gap-8">
      {!isMobileOrTablet && <HomeButton />}
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id=""
            tabs={MY_TEAM_TABS}
            selectedTabId={2}
            type="Team Applications"
          />
          <HomeButton />
          <Header />
        </>
      ) : (
        <>
          <Header />
          <TabNavbar
            id=""
            tabs={MY_TEAM_TABS}
            selectedTabId={2}
            type="Team Applications"
          />{" "}
        </>
      )}

      <div
        className={`${
          isMobileOrTablet ? "flex flex-col" : "flex flex-row"
        } gap-4 w-full`}
      >
        {!isMobileOrTablet && (
          <button
            onClick={() => setIsFilterOpen(true)}
            className="px-4 py-2 bg-[#961e38] rounded hover:bg-[#961e38] text-white w-[8vw]"
          >
            {t("Filter")}
          </button>
        )}

        {/* Search and Filter */}
        <div className="w-full flex justify-between items-center gap-2">
          {/* <SearchWithFilter
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            disabled={loading}
            isMobileOrTablet={isMobileOrTablet}
          /> */}
          {isMobileOrTablet && (
            <div>
              <button
                onClick={() => setIsFilterOpen(true)}
                className="px-4 py-2 bg-[#961e38] rounded hover:bg-[#961e38] text-white"
              >
                <BsFilter className="inline" />
              </button>
            </div>
          )}
        </div>

        {/* Filter Widget */}
        {isFilterOpen && (
          <div
            className={`absolute ${
              isMobileOrTablet ? "top-[41%]" : "top-[43%] left-[20%]"
            }  mt-2 bg-white shadow-lg rounded border w-80 p-4 z-50`}
            style={{ minWidth: "300px" }}
          >
            {/* Close button */}
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
              onClick={() => setIsFilterOpen(false)}
            >
              &#10005;
            </button>

            <div className="flex flex-col gap-2">
              {/* TYPE Dropdown */}
              <label>{t("Application Status")}</label>
              <select
                className="border p-2 rounded"
                value={applicationStatusFilter}
                onChange={(e) => {
                  setApplicationStatusFilter(e.target.value);
                }}
              >
                <option value="">{t("All")}</option>
                <option value="APPLIED">{t("Applied")}</option>
                <option value="APPROVED_BY_MANAGER">
                  {t("Approved By Manager")}
                </option>
                <option value="REJECTED_BY_MANAGER">
                  {t("Rejected By Manager")}
                </option>
                <option value="WAITING">{t("Waiting")}</option>
                <option value="DRAFTED">{t("Drafted")}</option>
                <option value="CONFIRMED_BY_MANAGER">
                  {t("Confirmed By Manager")}
                </option>
                <option value="CONFIRMED_BY_HR">{t("Confirmed By HR")}</option>
                <option value="CONFIRMED_BY_LEARNER">
                  {t("Confirmed By Learner")}
                </option>
                <option value="HR_APPROVAL_PENDING">
                  {t("HR Approval Pending")}
                </option>
                <option value="APPROVED_BY_HR">{t("HR Approved")}</option>
              </select>

              <label>{t("Course Name")}</label>
              <input
                type="text"
                className="border p-2 rounded"
                placeholder={t("Enter Course Name")}
                value={courseNameFilter}
                onChange={(e) => {
                  setCourseNameFilter(e.target.value);
                }}
              />

              <label>{t("Learner Name")}</label>
              <input
                type="text"
                className="border p-2 rounded"
                placeholder={t("Enter Learner Name")}
                value={learnerNameFilter}
                onChange={(e) => {
                  setLearnerNameFilter(e.target.value);
                }}
              />

              <button
                className="mt-2 text-sm bg-[#961e38] text-white py-2 px-4 rounded"
                onClick={() => {
                  setApplicationStatusFilter("");
                  setCourseNameFilter("");
                  setLearnerNameFilter("");
                }}
              >
                {t("Clear Filters")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile/Tablet View */}
      <div className="block md:hidden w-full">
        {loading ? (
          <div className="flex items-center w-full justify-center mt-4 mb-4">
            <SpinnerLoader />
          </div>
        ) : paginatedData.length > 0 ? (
          paginatedData.map((course, i) => {
            const { course_name, user_id } = course;
            return (
              <div
                key={user_id}
                className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
              >
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("User")}</h3>
                  <p className="text-lg">
                    {course.applications[0]?.first_name || ""}{" "}
                    {course.applications[0]?.last_name || ""}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Course Name")}</h3>
                  <p className="text-lg">{course.course_name}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2"> {t("Language")}</h3>
                  <p className="text-lg">
                    {course.applications[0].drafted_session_id
                      ? course.applications[0].session_language
                        ? course.applications[0].session_language
                        : "NOT AVAILABLE"
                      : "TBD"}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Career Level")}</h3>
                  <p className="text-lg">
                    {extractCareerLevelNumber(course.minimum_job_level)}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2"> {t("Session Details")}</h3>
                  <p className="text-lg">
                    {course.applications[0].drafted_session_id ? (
                      <span
                        className="text-blue-500 cursor-pointer underline"
                        onClick={() => {
                          setCurrentSessionName(
                            course.applications[0].session_name
                          );
                          handleOpenModal(course.applications[0].events);
                        }}
                      >
                        {t("Open")}
                      </span>
                    ) : (
                      t("TBD")
                    )}
                  </p>
                </div>
                <div className="flex justify-between items-center px-4 pt-2">
                  <div>
                    <h3 className="font-[600]">{t("Status")}</h3>
                    <div
                      className={`text-lg min-w-[10rem] text-sm p-2 text-center rounded ${
                        STATUS_MAP[course.applications[0].application_status]
                          ?.color || "bg-red-500 text-white"
                      }`}
                    >
                      {t(
                        STATUS_MAP[course.applications[0].application_status]
                          ?.label || "N/A"
                      )}
                    </div>
                  </div>{" "}
                  {course.applications[0].application_status !==
                    "REJECTED_BY_MANAGER" &&
                    course.applications[0].application_status !==
                      "CONFIRMED_BY_MANAGER" &&
                    course.applications[0].application_status !==
                      "CONFIRMED_BY_HR" &&
                    course.applications[0].application_status !==
                      "CONFIRMED_BY_LEARNER" && (
                      <button
                        style={{ color: "#961e38 !important" }}
                        onClick={() => {
                          setCurrentCourseId(course.course_id);
                          setCurrentUserId(course.applications[0].user_id);
                          setIsCancelApplicationModalOpen(true);
                          setCurrentUserName(
                            course.applications[0]?.first_name +
                              " " +
                              course.applications[0]?.last_name
                          );
                          setCurrentCourseName(course.course_name);
                        }}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                        title={t("More options")}
                      >
                        <FaX color="#961e38" size={20} />
                      </button>
                    )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="w-full text-center py-5">{t("No Data Available")}</p>
        )}
        {paginatedData?.length > 0 && !loading && (
          <div className="flex justify-between items-center w-full">
            <Pagination
              totalPages={totalPages}
              total_item_count={filteredCourses?.length || 0}
              _currentPage={currentPage}
              _itemsPerPage={itemsPerPage}
              handleChange={({ page, itemsPerPage }) => {
                setCurrentPage(page);
                setItemsPerPage(itemsPerPage);
              }}
              isCoursePage={true}
            />
          </div>
        )}
        {isCancelApplicationModalOpen && (
          <SaveConfirmModal
            closeModal={() => {
              setIsCancelApplicationModalOpen(false);
              setCurrentCourseName("");
              setCurrentUserName("");
            }}
            handleSave={handleCancelApplication}
            loading={updateLoading}
            msg={t("As a Manager, you can only delete [LEARNER NAME]'s application fully for [COURSE NAME]. If [LEARNER NAME] wishes to reject a seat they were offered, they should do so themselves on their Application Status page.")
              .replace(/\[LEARNER NAME\]/g, `<strong>${currentUserName}</strong>`)
              .replace("[COURSE NAME]", `<strong>${currentCourseName}</strong>`)}
            saveBtnText={t("DELETE APPLICATION")}
            cancleBtnText={t("GO BACK")}
          />
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block w-full h-full">
        <table className="w-full border-collapse">
          <thead className="bg-[#961e38] w-full text-white rounded-t-xl">
            <tr>
              <th className="py-2 px-2 text-center">{t("User")}</th>
              <th className="py-2 px-2 text-center">{t("Course Name")}</th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Language")}
              </th>
              <th className="py-2 px-2 text-center">{t("Career Level")}</th>
              <th className="py-2 px-2 text-center">{t("Session Details")}</th>
              <th className="py-2 px-2 text-center">{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6} // Ensure it spans all columns
                  className="py-5 text-center mt-4 mb-4" // Center the content inside the cell
                >
                  <SpinnerLoader />
                </td>
              </tr>
            ) : (
              <>
                {paginatedData.length > 0 ? (
                  paginatedData.map((course: any, i) => (
                    <tr key={i}>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-left">
                        {course.applications[0]?.first_name || ""}{" "}
                        {course.applications[0]?.last_name || ""}
                      </td>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                        {course.course_name}
                      </td>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                        {course.applications[0].drafted_session_id
                          ? course.applications[0].session_language
                            ? course.applications[0].session_language
                            : "NOT AVAILABLE"
                          : "TBD"}
                      </td>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                        {extractCareerLevelNumber(course.minimum_job_level)}
                      </td>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                        {course.applications[0].events ? (
                          <span
                            className="text-blue-500 cursor-pointer underline"
                            onClick={() => {
                              setCurrentSessionName(
                                course.applications[0].session_name
                              );
                              handleOpenModal(course.applications[0].events);
                            }}
                          >
                            {t("Open")}
                          </span>
                        ) : (
                          t("TBD")
                        )}
                      </td>
                      <td className="border-b border-[#c6c6c6] py-2 px-4 text-center align-middle">
                        <div className="flex justify-center items-center gap-4">
                          <div
                            className={`text-lg min-w-[10rem] text-sm p-2 text-center rounded ${
                              STATUS_MAP[
                                course.applications[0].application_status
                              ]?.color || "bg-red-500 text-white"
                            }`}
                          >
                            {t(
                              STATUS_MAP[
                                course.applications[0].application_status
                              ]?.label || "N/A"
                            )}
                          </div>
                          {course.applications[0].application_status !==
                            "REJECTED_BY_MANAGER" &&
                            course.applications[0].application_status !==
                              "CONFIRMED_BY_MANAGER" &&
                            course.applications[0].application_status !==
                              "CONFIRMED_BY_HR" &&
                            course.applications[0].application_status !==
                              "CONFIRMED_BY_LEARNER" && (
                              <button
                                className={`ml-4 p-2 text-red-500`}
                                onClick={() => {
                                  setCurrentCourseId(course.course_id);
                                  setCurrentUserId(
                                    course.applications[0].user_id
                                  );
                                  setIsCancelApplicationModalOpen(true);
                                  setCurrentUserName(
                                    course.applications[0]?.first_name +
                                      " " +
                                      course.applications[0]?.last_name
                                  );
                                  setCurrentCourseName(course.course_name);
                                }}
                              >
                                <FaX size={20} />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2}>
                      <p className="w-full text-center py-5">
                        {t("No Data Available")}
                      </p>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>

        {paginatedData?.length > 0 && !loading && (
          <div className="flex justify-between items-center w-full">
            <Pagination
              isCoursePage={true}
              totalPages={totalPages}
              total_item_count={filteredCourses?.length || 0}
              handleChange={({ page, itemsPerPage }) => {
                setCurrentPage(page);
                setItemsPerPage(itemsPerPage);
              }}
              _currentPage={currentPage}
              _itemsPerPage={itemsPerPage}
            />
          </div>
        )}
        {isCancelApplicationModalOpen && (
          <SaveConfirmModal
            closeModal={() => {
              setIsCancelApplicationModalOpen(false);
              setCurrentCourseName("");
              setCurrentUserName("");
            }}
            handleSave={handleCancelApplication}
            loading={updateLoading}
            msg={t("As a Manager, you can only delete [LEARNER NAME]'s application fully for [COURSE NAME]. If [LEARNER NAME] wishes to reject a seat they were offered, they should do so themselves on their Application Status page.")
              .replace(/\[LEARNER NAME\]/g, `<strong>${currentUserName}</strong>`)
              .replace("[COURSE NAME]", `<strong>${currentCourseName}</strong>`)}
            saveBtnText={t("DELETE APPLICATION")}
            cancleBtnText={t("GO BACK")}
          />
        )}
      </div>
      <EventDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalData={modalData}
        sessionName={currentSessionName}
        modalName="Session Details"
      />
    </div>
  );
};

export default TeamApplications;
